import { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useMyReviews } from "@/hooks/useApiData";
import { guestApi } from "@/lib/api";

export const Reviews = () => {
  const { eligible, reviews: revs, setReviews: setRevs, testis, setTestis, loading, error } = useMyReviews();

  return (
    <div data-testid="reviews-root">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">Account · Reviews & testimonials</p>
      <h1 className="mt-3 font-display text-4xl font-medium leading-[0.95] tracking-tight md:mt-4 md:text-6xl">Share your <span className="italic">stay</span>.</h1>
      <p className="mt-6 max-w-xl text-sm text-muted-foreground md:text-base">A <strong className="text-foreground">testimonial</strong> is for StayEasy as a platform. A <strong className="text-foreground">property review</strong> is for the specific home you stayed in.</p>

      {loading ? (
        <div className="mt-12 animate-pulse space-y-8">
          <div className="h-5 w-36 bg-muted" />
          <div className="h-72 bg-muted" />
          <div className="h-5 w-44 bg-muted" />
          <div className="h-36 bg-muted" />
        </div>
      ) : error ? (
        <div className="mt-12 border border-dashed border-foreground/20 p-10 text-center">
          <p className="font-display text-3xl">Reviews unavailable.</p>
          <p className="mt-2 text-sm text-muted-foreground">We could not load your review history. Refresh the page to try again.</p>
        </div>
      ) : (
        <>
          <section className="mt-12 border-t border-foreground/10 pt-10 md:mt-14">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">Ready to review</p>
            {eligible.length === 0 ? (
              <div className="mt-6 border border-dashed border-foreground/20 p-8 text-center md:p-12">
                <p className="font-display text-2xl md:text-3xl">Nothing to review yet.</p>
                <p className="mt-2 text-sm text-muted-foreground">Completed stays will appear here for feedback.</p>
              </div>
            ) : eligible.map((b) => (
              <ReviewForm key={b.id} booking={b} onSubmit={(payload) => {
                if (payload.kind === "property") setRevs((r) => [{ ...payload, id: `R-${Date.now()}`, status: "pending" }, ...(r || [])]);
                else setTestis((t) => [{ ...payload, id: `T-${Date.now()}`, status: "pending" }, ...(t || [])]);
              }} />
            ))}
          </section>

          <section className="mt-16 border-t border-foreground/10 pt-10">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">Your property reviews</p>
            <div className="mt-6 space-y-4">{revs.length === 0 ? <p className="text-sm text-muted-foreground">Nothing submitted yet.</p> : revs.map((r) => <SubmittedCard key={r.id} title={r.title || "Property review"} subtitle={r.property?.name || r.property?.title} content={r.content} status={r.status} rating={r.rating} to={r.property?.id ? `/properties/${r.property.id}` : undefined} />)}</div>
          </section>

          <section className="mt-12 border-t border-foreground/10 pt-10">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">Your testimonials</p>
            <div className="mt-6 space-y-4">{testis.length === 0 ? <p className="text-sm text-muted-foreground">Nothing submitted yet.</p> : testis.map((t) => <SubmittedCard key={t.id} title={t.title || "Testimonial"} subtitle="For StayEasy" content={t.content} status={t.status} rating={t.rating} />)}</div>
          </section>
        </>
      )}
    </div>
  );
};

const ReviewForm = ({ booking, onSubmit }) => {
  const [kind, setKind] = useState("property");
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Give a star rating");
    if (content.trim().length < 20) return toast.error("A few more words would help.");
    const payload = { booking_id: booking.id, rating, title, content };
    setSending(true);
    try {
      if (kind === "property") await guestApi.submitPropertyReview(payload);
      else await guestApi.submitTestimonial(payload);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.apiMessage || "Could not submit review.");
      setSending(false);
      return;
    }
    onSubmit({ kind, bookingId: booking.id, property: kind === "property" ? booking.property : undefined, rating, title, content, submittedAt: new Date().toLocaleDateString() });
    toast.success("Thanks — submitted for moderation.");
    setRating(0); setTitle(""); setContent(""); setSending(false);
  };

  return (
    <form onSubmit={submit} data-testid={`review-form-${booking.id}`} className="mt-6 border border-foreground/10 bg-card p-6 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><p className="font-display text-2xl font-medium tracking-tight">{booking.property.name}</p><p className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">{booking.property.location} · Stayed {new Date(`${booking.checkIn}T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</p></div>
        <div className="flex gap-2"><KindPill active={kind === "property"} onClick={() => setKind("property")}>Property review</KindPill><KindPill active={kind === "testimonial"} onClick={() => setKind("testimonial")}>Platform testimonial</KindPill></div>
      </div>
      <div className="mt-6 flex items-center gap-1">{[1,2,3,4,5].map((n) => <button key={n} type="button" data-testid={`star-${n}`} onClick={() => setRating(n)} aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`} className="p-1"><Star size={22} className={n <= rating ? "fill-primary stroke-primary" : "stroke-foreground/30"} /></button>)}<span className="ml-3 text-xs text-muted-foreground">{rating ? `${rating} / 5` : "Tap to rate"}</span></div>
      <input data-testid={`review-title-${booking.id}`} type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A short headline (optional)" className="mt-6 w-full border-b border-foreground/20 bg-transparent py-3 text-base focus:border-foreground focus:outline-none" />
      <textarea data-testid={`review-content-${booking.id}`} value={content} onChange={(e) => setContent(e.target.value)} rows={4} placeholder="What was memorable? What could have been better?" className="mt-4 w-full resize-none border border-foreground/15 bg-transparent p-3 text-sm focus:border-foreground focus:outline-none" />
      <div className="mt-5 flex items-center justify-end"><button type="submit" disabled={sending} data-testid={`review-submit-${booking.id}`} className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-50">{sending ? "Submitting…" : "Submit for moderation"}</button></div>
    </form>
  );
};

const KindPill = ({ active, onClick, children }) => <button type="button" onClick={onClick} className={`rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors ${active ? "border-foreground bg-foreground text-background" : "border-foreground/15 text-muted-foreground hover:text-foreground"}`}>{children}</button>;

const SubmittedCard = ({ title, subtitle, content, status, rating, to }) => {
  const statusMap = { published: { label: "Published", cls: "bg-primary/10 text-primary" }, pending: { label: "Under review", cls: "bg-muted text-muted-foreground" }, rejected: { label: "Not published", cls: "bg-foreground/10 text-foreground" } };
  const s = statusMap[status] || statusMap.pending;
  const Wrapper = to ? Link : "div";
  return <Wrapper to={to} className="block border border-foreground/10 bg-card p-6"><div className="flex flex-wrap items-center justify-between gap-3"><div><p className="font-display text-lg font-medium tracking-tight md:text-xl">{title}</p><p className="mt-0.5 text-xs uppercase tracking-[0.15em] text-muted-foreground">{subtitle}</p></div><div className="flex items-center gap-3"><span className="inline-flex items-center gap-1 text-sm"><Star size={12} className="fill-foreground stroke-foreground" />{rating}</span><span className={`rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] ${s.cls}`}>{s.label}</span></div></div><p className="mt-3 text-sm leading-relaxed text-muted-foreground">{content}</p></Wrapper>;
};

export default Reviews;
